import { css } from '@emotion/core'
import { ContentRemove, NoticeDoNot } from '@fs/zion-icon/dist/cjs/icons'
import {
  ActionChip,
  Button,
  Cell,
  Checkbox,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  FlowGrid,
  Grid,
  Row,
  colors,
  useOverlay,
  H5,
} from '@fs/zion-ui'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getGroupTreeContributions } from '../../../services/groupsService'

const clickable = css`
  cursor: pointer;
`
export default function TreeChangesFiltersButton({
  treeId,
  filtersState,
  setFiltersState,
  treeContributionsData,
  setTreeContributionsData,
}) {
  const overlay = useOverlay()
  const { t } = useTranslation()

  const updateTreeChangesData = (data) => {
    setTreeContributionsData(data)
  }

  function checkboxOnChange(dropdownKey, accordionKey, event) {
    event.stopPropagation()
    const accordionItems = filtersState[dropdownKey].accordionItems
    if (dropdownKey === 'contributorId' || dropdownKey === 'conclusionType') {
      Object.keys(accordionItems).forEach((accordionkey) => {
        const accordionItem = accordionItems[accordionkey]
        accordionItem.isChecked = false
      })
    }
    accordionItems[accordionKey].isChecked = event.target.checked
    setFiltersState(filtersState)
  }

  const applyFiltersButtonOnClick = () => {
    const filters = {
      type: '',
      subtypes: [],
    }
    Object.keys(filtersState)
      .filter((dropdownKey) => dropdownKey !== 'currentFilters')
      .forEach((dropdownKey) => {
        const dropdownItem = filtersState[dropdownKey]
        if (dropdownItem.isOpen) {
          filters.type = dropdownKey
          Object.keys(dropdownItem.accordionItems).forEach((accordionKey) => {
            const accordionItem = dropdownItem.accordionItems[accordionKey]
            if (accordionItem.isChecked) {
              filters.subtypes.push(accordionItem)
            }
          })
        }
      })
    filtersState.currentFilters = filters
    filtersState.currentFilters.subtypes = filtersState.currentFilters.subtypes.map((subtype) => {
      const valueWithLabel = { value: subtype.value, label: subtype.label }
      switch (filtersState.currentFilters.type) {
        case 'contributorId': {
          valueWithLabel.label = t('contributorIdWithValue', {
            defaultValue: `Contributor ID: ${subtype.value}`,
            contributorId: subtype.value,
          })
          break
        }
        case 'omitContributorId': {
          valueWithLabel.label = t('omitContributorIdWithValue', {
            defaultValue: `Omit Contributor ID: ${subtype.value}`,
            contributorId: subtype.value,
          })
          break
        }
        default:
          break
      }
      return valueWithLabel
    })
    setFiltersState(filtersState)
    getGroupTreeContributions(
      treeId,
      filters.type,
      filtersState.currentFilters.subtypes.map((value) => value.value)
    ).then((response) => {
      updateTreeChangesData(response.data)
    })
    overlay.close()
  }

  const clearFiltersButtonOnClick = () => {
    Object.keys(filtersState)
      .filter((dropdownKey) => dropdownKey !== 'currentFilters')
      .forEach((dropdownKey) => {
        const dropdownItem = filtersState[dropdownKey]
        dropdownItem.isOpen = false
        Object.keys(dropdownItem.accordionItems).forEach((accordionKey) => {
          const accordionItem = dropdownItem.accordionItems[accordionKey]
          accordionItem.isChecked = false
        })
      })
    filtersState.currentFilters = {
      type: '',
      subtypes: [],
    }
    setFiltersState(filtersState)
    getGroupTreeContributions(treeId).then((response) => {
      updateTreeChangesData(response.data)
    })
    overlay.close()
  }

  const onFilterChipClick = (value) => {
    filtersState.currentFilters.subtypes = filtersState.currentFilters.subtypes.filter(
      (subtype) => subtype.value !== value
    )
    const itemToDeselect = Object.values(filtersState[filtersState.currentFilters.type].accordionItems).find(
      (item) => item.value === value
    )
    itemToDeselect.isChecked = false
    if (filtersState.currentFilters.subtypes.length > 0) {
      getGroupTreeContributions(
        treeId,
        filtersState.currentFilters.type,
        filtersState.currentFilters.subtypes.map((s) => s.value)
      ).then((response) => {
        updateTreeChangesData(response.data)
      })
    } else {
      filtersState.currentFilters.type = ''
      getGroupTreeContributions(treeId).then((response) => {
        updateTreeChangesData(response.data)
      })
    }
    setFiltersState(filtersState)
  }

  function expansionPanelOnClick(currentDropdownItem) {
    Object.keys(filtersState)
      .filter((dropdownKey) => dropdownKey !== 'currentFilters')
      .forEach((dropdownKey) => {
        const dropdownItem = filtersState[dropdownKey]
        if (dropdownItem.label === currentDropdownItem.label) {
          dropdownItem.isOpen = !currentDropdownItem.isOpen // toggle
        } else {
          // clean the rest
          dropdownItem.isOpen = false
          Object.keys(dropdownItem.accordionItems).forEach((accordionKey) => {
            const accordionItem = dropdownItem.accordionItems[accordionKey]
            accordionItem.isChecked = false
          })
        }
      })
    setFiltersState(filtersState)
  }

  useEffect(() => {
    treeContributionsData?.tfUserContributions.contributions.forEach((contribution) => {
      const accordionItem = {
        label: contribution.contributorId,
        value: contribution.contributorId,
        isChecked: false,
      }
      filtersState.contributorId.accordionItems[contribution.contributorId] = accordionItem
      filtersState.omitContributorId.accordionItems[contribution.contributorId] = accordionItem
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeContributionsData?.tfUserContributions.contributions])

  return (
    <div>
      <Row alignY="middle">
        <Button emphasis="medium" onClick={overlay.handleClick} noShrinkX>
          {t('filters.label', 'Filters')}
        </Button>
        <Row wrap gutters="nano">
          {filtersState.currentFilters.subtypes.map((value) => (
            <ActionChip
              key={value.label}
              Icon={ContentRemove}
              label={value.label}
              onClick={() => onFilterChipClick(value.value)}
            />
          ))}
        </Row>
      </Row>
      <DialogOverlay title="" {...overlay} size="xl">
        <Grid>
          {/* Row */}
          <Cell columns={12}>
            {Object.keys(filtersState)
              .filter((dropdownKey) => dropdownKey !== 'currentFilters')
              .map((dropdownKey) => {
                const dropdownItem = filtersState[dropdownKey]
                return (
                  <div key={dropdownKey} data-testid={`expander-${dropdownItem.label}`}>
                    <div css={clickable}>
                      <H5
                        data-testid={`button-${dropdownItem.label}`}
                        onClick={() => expansionPanelOnClick(dropdownItem)}
                      >
                        {dropdownItem.label}
                      </H5>
                    </div>
                    {dropdownItem.isOpen && (
                      <div>
                        <FlowGrid columnCount={3} orientation="vertical" gutters="md">
                          {Object.keys(dropdownItem.accordionItems).map((accordionKey) => {
                            const accordionItem = dropdownItem.accordionItems[accordionKey]
                            return (
                              <React.Fragment key={accordionItem.label}>
                                <Checkbox
                                  id={accordionItem.value}
                                  name="thisIsNotUsedButZionWantsToHaveOne"
                                  label={
                                    <>
                                      {accordionItem.accountIsEnabled === false && (
                                        <>
                                          <NoticeDoNot color={colors.red50} data-testid="disabledAccountIcon" />
                                          {` `}
                                        </>
                                      )}
                                      {accordionItem.label}
                                    </>
                                  }
                                  checked={accordionItem.isChecked}
                                  color="primary"
                                  onChange={(event) => checkboxOnChange(dropdownKey, accordionKey, event)}
                                />
                              </React.Fragment>
                            )
                          })}
                        </FlowGrid>
                      </div>
                    )}
                  </div>
                )
              })}
          </Cell>
        </Grid>
        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button onClick={applyFiltersButtonOnClick} emphasis="high">
              {t('person.buttons.applyFilters', 'Apply Filters')}
            </Button>
            <Button onClick={clearFiltersButtonOnClick} emphasis="medium">
              {(t('filters.clear'), 'Clear Filters')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </div>
  )
}
